import { Button, Heading, IconButton } from '@assembly-js/design-system';
import { useTranslation } from 'react-i18next';

import { useMediaQuery } from 'src/hooks/useMediaQuery';
import { ModalWrapper } from 'src/legacy/components/Modals';
import type {
  WhatsNewAnnouncement,
  WhatsNewButton,
} from 'src/services/api/whatsNewApi';

const DESKTOP_WIDTH_PX = 764;
const MOBILE_MAX_WIDTH_PX = 368;
const PAPER_RADIUS_PX = 8;
const PAPER_SHADOW = '0px 6px 20px 0px rgba(0, 0, 0, 0.07)';
const MOBILE_GUTTER_PX = 16;

interface WhatsNewModalProps {
  announcement: WhatsNewAnnouncement;
  /** Fired for every way out of the modal — X, backdrop, escape, or either button. */
  onDismiss: () => void;
  /** Follows a button's link; the modal dismisses itself first. */
  onOpenLink: (url: string) => void;
}

/**
 * Short product announcement: a "New" tag, a header and up to three feature
 * rows beside an image, with zero, one or two CTAs. Desktop lays the copy
 * beside the image; mobile stacks the image above the copy.
 */
export const WhatsNewModal = ({
  announcement,
  onDismiss,
  onOpenLink,
}: WhatsNewModalProps) => {
  const { t } = useTranslation();
  const isMobile = useMediaQuery('sm', 'max');
  const {
    header,
    bullets,
    imageUrl,
    imageAlt,
    primaryButton,
    secondaryButton,
  } = announcement;

  const handleButtonClick = (button: WhatsNewButton) => {
    onDismiss();
    onOpenLink(button.url);
  };

  return (
    <ModalWrapper
      open
      onClose={onDismiss}
      hideActions
      hideDividers
      disableGutters
      fullWidth={isMobile}
      width={isMobile ? undefined : DESKTOP_WIDTH_PX}
      height="auto"
      aria-label={header}
      // ModalWrapper's paper radius/shadow don't match this Figma modal, so
      // override them here rather than adding props to the shared wrapper.
      sx={{
        '& .MuiDialog-paper': {
          m: `${MOBILE_GUTTER_PX}px`,
          // Pin the mobile width so the Figma 16px gutters hold on any phone;
          // otherwise ModalWrapper and MUI race to set it.
          ...(isMobile && {
            width: `calc(100% - ${MOBILE_GUTTER_PX * 2}px)`,
          }),
          maxWidth: isMobile ? MOBILE_MAX_WIDTH_PX : DESKTOP_WIDTH_PX,
          borderRadius: `${PAPER_RADIUS_PX}px`,
          boxShadow: PAPER_SHADOW,
          border: 'none',
          overflow: 'hidden',
        },
      }}
    >
      <div className="relative flex w-full flex-col-reverse bg-white sm:min-h-[414px] sm:flex-row">
        <IconButton
          icon="Close"
          variant="secondary"
          aria-label={t(($) => $.components.whats_new.close)}
          onClick={onDismiss}
          className="absolute right-4 top-[11px] z-10 sm:right-[13px] sm:top-3 !h-6 !w-6 !rounded-full !border-0 !bg-gray-100 !p-1 !text-gray-small opacity-[0.74]"
        />

        <div className="flex flex-col gap-8 px-4 pb-5 pt-4 sm:flex-1 sm:gap-6 sm:p-6">
          <div className="flex flex-col gap-2 sm:flex-1 sm:gap-3">
            {/* The design system Tag is a 13px pill; Figma uses the square 12px "Tab-items" chip. */}
            <span className="self-start rounded bg-info-background px-1.5 py-0.5 text-[12px] leading-5 text-[#053299]">
              {t(($) => $.components.whats_new.new_badge)}
            </span>
            <div className="flex flex-col gap-6 sm:gap-8">
              <Heading
                tag="h2"
                size="lg"
                className="!m-0 !leading-6 text-black-heading sm:min-h-[55px] sm:!text-[20px]"
              >
                {header}
              </Heading>
              {bullets.length > 0 && (
                <ul className="m-0 flex list-disc flex-col gap-3 pl-[23px] sm:gap-4 text-[13px] leading-[21px] sm:leading-[18px] text-gray-small">
                  {bullets.map((bullet) => (
                    <li key={bullet}>{bullet}</li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          {primaryButton && (
            <div className="flex flex-col gap-3 sm:gap-2">
              <Button
                variant="primary"
                size="sm"
                className="w-full !border-black/[0.12]"
                label={primaryButton.label}
                onClick={() => handleButtonClick(primaryButton)}
              />
              {secondaryButton && (
                <Button
                  variant="secondary"
                  size="sm"
                  className="w-full !border-black/[0.12]"
                  label={secondaryButton.label}
                  onClick={() => handleButtonClick(secondaryButton)}
                />
              )}
            </div>
          )}
        </div>

        <div className="relative h-[170px] w-full shrink-0 overflow-hidden bg-black sm:h-auto sm:w-[380px]">
          <img
            src={imageUrl}
            alt={imageAlt}
            decoding="async"
            className="absolute inset-0 h-full w-full object-cover object-top sm:object-center"
          />
        </div>
      </div>
    </ModalWrapper>
  );
};
